import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import CollegeBatchDataUpdate from './CollegeBatchDataUpdate';
import Breadcrumb from '../Shared/Breadcrumb';

// Utility function to parse date strings as local dates (fixes timezone offset issue)
const parseLocalDate = (dateString) => {
  if (!dateString) return new Date();
  // Append T00:00:00 to ensure it's parsed as local time, not UTC
  const localDateString = dateString.includes('T') ? dateString : `${dateString}T00:00:00`;
  return new Date(localDateString);
};

import { 
  ArrowLeft, 
  Save, 
  BarChart3, 
  ChevronDown, 
  ChevronUp,
  Plus,
  Calendar,
  Target,
  Brain,
  FileText,
  Mail,
  PenTool,
  GraduationCap,
  ExternalLink,
  MapPin,
  Users, 
  Heart,
  TrendingUp,
  Clock,
  CheckCircle,
  CheckSquare,
  Edit3,
  X,
  Star,
  Award,
  DollarSign,
  Bookmark,
  Search,
  AlertCircle,
  Sparkles,
  BookOpen
} from 'lucide-react';

// Enhanced styling to match the mockup
const sectionStyles = {
  urgent: 'border-l-4 border-l-red-500',
  valueProposition: 'border-l-4 border-l-violet-500', 
  research: 'border-l-4 border-l-amber-500',
  strategic: 'border-l-4 border-l-teal-500',
  essays: 'border-l-4 border-l-indigo-500'
};

const CollegeStrategy = () => {
  const { collegeId } = useParams();
  
  console.log('🎓 CollegeStrategy component mounted with collegeId:', collegeId);
  console.log('🌐 Current URL:', window.location.href);
  const {
    colleges,
    updateCollege,
    updateCollegeStrategy,
    timeline,
    addTimelineItem,
    addEssay,
    getEssaysByCollege,
    getCollegeDeadlines,
    loadCollegeDeadlines,
    addCollegeDeadline,
    deleteCollegeDeadline,
    generateImmediatePriorities,
    updateStudentDeadline,
    updateStudentEssayPrompts,
    updateStudentRequirements,
    getVerificationStatus,
    loading,
    COLLEGE_CATEGORIES,
    APPLICATION_ROUNDS,
    ESSAY_TYPES
  } = useCollegeContext();
  
  const { currentUser, familyUsers, switchUser } = useFamilyContext();

  const [autosaveTimers, setAutosaveTimers] = useState({});
  
  // Autosave helper with debouncing
  const handleAutosave = (field, value) => {
    // Clear existing timer for this field
    if (autosaveTimers[field]) {
      clearTimeout(autosaveTimers[field]);
    }
    
    // Set new timer
    const timer = setTimeout(() => {
      updateCollegeStrategy(collegeId, { [field]: value });
      setAutosaveTimers(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }, 1000); // 1 second debounce
    
    setAutosaveTimers(prev => ({ ...prev, [field]: timer }));
  };
  
  const [editing, setEditing] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showAddEssay, setShowAddEssay] = useState(false);
  const [showUpdateData, setShowUpdateData] = useState(false);
  const [showAddDeadline, setShowAddDeadline] = useState(false);
  
  // Expandable sections state
  const [expandedSections, setExpandedSections] = useState({
    todos: true,
    valueProposition: false,
    research: false,
    strategic: false,
    academics: false,  // Collapsed since test scores moved to strategic
    financial: true,   // Expanded by default to show data
    requirements: true // Expanded by default to show data
  });
  const [newMilestone, setNewMilestone] = useState({
    title: '',
    due_date: '',
    category: 'applications',
    description: ''
  });
  const [newEssay, setNewEssay] = useState({
    title: '',
    prompt: '',
    type: 'supplemental',
    word_limit: ''
  });

  const [newDeadline, setNewDeadline] = useState({
    title: '',
    due_date: '',
    deadline_type: 'early_decision',
    description: '',
    urgency_level: 'normal'
  });

  // Student verification state
  const [editingDeadline, setEditingDeadline] = useState(null); // Track which deadline is being edited
  const [editingDeadlineValue, setEditingDeadlineValue] = useState('');
  const [verificationStatus, setVerificationStatus] = useState({
    deadlines: false,
    essays: false,
    requirements: false
  });

  // Common deadline templates
  const commonDeadlines = [
    { type: 'early_decision', title: 'Early Decision Application Due', urgency: 'critical', description: 'Binding early admission deadline' },
    { type: 'early_action', title: 'Early Action Application Due', urgency: 'urgent', description: 'Non-binding early admission deadline' },
    { type: 'regular_decision', title: 'Regular Decision Application Due', urgency: 'urgent', description: 'Regular admission deadline' },
    { type: 'recommendations_due', title: 'Recommendation Letters Due', urgency: 'urgent', description: 'Teacher/counselor recommendations deadline' },
    { type: 'decisions_released', title: 'Admission Decisions Released', urgency: 'normal', description: 'When admission decisions are announced' },
    { type: 'enrollment_deposit', title: 'Enrollment Deposit Due', urgency: 'critical', description: 'Deadline to secure your spot' },
    { type: 'financial_aid', title: 'Financial Aid Application Due', urgency: 'urgent', description: 'FAFSA, CSS Profile, or school forms due' },
    { type: 'scholarship', title: 'Scholarship Application Due', urgency: 'urgent', description: 'Merit-based scholarship deadline' },
    { type: 'housing', title: 'Housing Application Due', urgency: 'normal', description: 'Residence hall application deadline' },
    { type: 'orientation', title: 'Orientation Registration Due', urgency: 'normal', description: 'New student orientation signup' },
    { type: 'custom', title: 'Custom Deadline', urgency: 'normal', description: 'Add your own deadline' }
  ];

  const college = colleges.find(c => c.id === collegeId);

  // Debug logging
  console.log('🎓 CollegeStrategy Debug:', {
    collegeId,
    collegesCount: colleges.length,
    collegeFound: !!college,
    collegeName: college?.name,
    loading,
    collegeIds: colleges.map(c => ({ id: c.id, name: c.name }))
  });

  // Load deadlines when component mounts or collegeId changes
  useEffect(() => {
    if (collegeId) {
      loadCollegeDeadlines(collegeId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collegeId]); // Removed loadCollegeDeadlines from dependencies to prevent infinite loop

  // Load verification status when college changes
  useEffect(() => {
    if (collegeId && getVerificationStatus) {
      getVerificationStatus(collegeId).then(status => {
        setVerificationStatus(status);
      });
    }
  }, [collegeId, getVerificationStatus]);
  
  // Debug logging for college data
  console.log('🎓 CollegeStrategy Debug:', {
    collegeId,
    college,
    loading,
    student_advantages: college?.student_advantages,
    institutional_priorities: college?.institutional_priorities,
    test_score_requirements: college?.test_score_requirements,
    acceptance_rate: college?.acceptance_rate,
    sat_range: college?.sat_range,
    avg_gpa_weighted: college?.avg_gpa_weighted,
    tuition: college?.tuition,
    all_college_keys: college ? Object.keys(college).sort() : []
  });

  // Show loading while college data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading college data...</p>
        </div>
      </div>
    );
  }
  
  if (!college) {
    console.log('❌ College not found for ID:', collegeId);
    console.log('📋 Available colleges:', colleges.map(c => ({ id: c.id, name: c.name })));
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-900 mb-2">College Not Found</h2>
          <p className="text-slate-600 mb-4">College ID: {collegeId}</p>
          <p className="text-slate-600 mb-4">Available colleges: {colleges.length}</p>
          <Link to="/college" className="text-azure-600 hover:text-azure-700">
            Return to College Journey Hub
          </Link>
        </div>
      </div>
    );
  }

  // Get real data
  const collegeEssays = getEssaysByCollege(collegeId);
  const collegeTimeline = timeline.filter(item => item.college_id === collegeId);
  const collegeDeadlines = getCollegeDeadlines(collegeId);
  const immediatePriorities = generateImmediatePriorities(collegeId);
  
  // Get deadlines from college record fields if no separate deadlines exist
  const getCollegeDeadlineFields = () => {
    if (!college) return [];
    
    const deadlineFields = [
      { key: 'ed_deadline', label: 'Early Decision Deadline' },
      { key: 'ed2_deadline', label: 'Early Decision II Deadline' },
      { key: 'ea_deadline', label: 'Early Action Deadline' },
      { key: 'rd_deadline', label: 'Regular Decision Deadline' },
      { key: 'finaid_deadline', label: 'Financial Aid Deadline' },
      { key: 'css_deadline', label: 'CSS Profile Deadline' },
      { key: 'merit_deadline', label: 'Merit Scholarship Deadline' },
      { key: 'honors_deadline', label: 'Honors Program Deadline' }
    ];
    
    return deadlineFields
      .filter(field => college[field.key])
      .map(field => ({
        id: field.key,
        title: field.label,
        due_date: college[field.key],
        urgency_level: 'normal'
      }))
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };
  
  // Use separate deadlines if they exist, otherwise use college deadline fields
  const displayDeadlines = collegeDeadlines.length > 0 ? collegeDeadlines : getCollegeDeadlineFields();
  
  const startEditing = (field, currentValue) => {
    setEditing(field);
    setEditValues({ [field]: currentValue });
  };

  const saveEdit = (field) => {
    if (field.startsWith('strategy.')) {
      const strategyField = field.replace('strategy.', '');
      updateCollegeStrategy(collegeId, { [strategyField]: editValues[field] });
    } else {
      updateCollege(collegeId, { [field]: editValues[field] });
    }
    setEditing(null);
    setEditValues({});
  };

  const handleSave = (field) => {
    const value = editValues[field];
    if (value !== undefined) {
      updateCollege(collegeId, { [field]: value });
      setEditing(null);
      setEditValues({});
    }
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditValues({});
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleAddMilestone = () => {
    if (!newMilestone.title.trim() || !newMilestone.due_date) return;
    
    addTimelineItem({
      ...newMilestone,
      college_id: collegeId
    });
    
    setNewMilestone({
      title: '',
      due_date: '',
      category: 'applications',
      description: ''
    });
    setShowAddMilestone(false);
  };

  const handleAddEssay = () => {
    if (!newEssay.title.trim() && !newEssay.prompt.trim()) return;
    
    addEssay({
      ...newEssay,
      college_id: collegeId,
      title: newEssay.title || newEssay.prompt.substring(0, 50) + '...'
    });
    
    setNewEssay({
      title: '',
      prompt: '',
      type: 'supplemental',
      word_limit: ''
    });
    setShowAddEssay(false);
  };

  const handleAddDeadline = async () => {
    if (!newDeadline.title.trim() || !newDeadline.due_date) return;

    try {
      await addCollegeDeadline({
        title: newDeadline.title,
        description: newDeadline.description,
        due_date: newDeadline.due_date,
        deadline_type: newDeadline.deadline_type,
        urgency_level: newDeadline.urgency_level,
        is_official: true
      }, collegeId);

      setNewDeadline({
        title: '',
        due_date: '',
        deadline_type: 'application',
        description: '',
        urgency_level: 'normal'
      });
      setShowAddDeadline(false);
    } catch (error) {
      console.error('Error adding deadline:', error);
      alert('Failed to add deadline. Please try again.');
    }
  };

  // Student deadline editing handlers
  const handleEditDeadline = (deadlineType, currentValue) => {
    setEditingDeadline(deadlineType);
    setEditingDeadlineValue(currentValue || '');
  };

  const handleSaveDeadline = async (deadlineType) => {
    if (!editingDeadlineValue) {
      setEditingDeadline(null);
      return;
    }

    try {
      const result = await updateStudentDeadline(collegeId, deadlineType, editingDeadlineValue);
      if (result.success) {
        // Refresh verification status
        const status = await getVerificationStatus(collegeId);
        setVerificationStatus(status);
        setEditingDeadline(null);
        setEditingDeadlineValue('');
      } else {
        alert(`Failed to update deadline: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving deadline:', error);
      alert('Failed to save deadline. Please try again.');
    }
  };

  const handleCancelEditDeadline = () => {
    setEditingDeadline(null);
    setEditingDeadlineValue('');
  };

  const AddEssayModal = () => {
    if (!showAddEssay) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Add Essay</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Essay Title (optional)</label>
              <input
                type="text"
                value={newEssay.title}
                onChange={(e) => setNewEssay({ ...newEssay, title: e.target.value })}
                placeholder="e.g., Why Duke?"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Essay Prompt</label>
              <textarea
                value={newEssay.prompt}
                onChange={(e) => setNewEssay({ ...newEssay, prompt: e.target.value })}
                placeholder="Enter the essay prompt or question..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 resize-none"
                autoFocus
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
                <select
                  value={newEssay.type}
                  onChange={(e) => setNewEssay({ ...newEssay, type: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                >
                  {Object.entries(ESSAY_TYPES).map(([key, type]) => (
                    <option key={key} value={key}>{type.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Word Limit</label>
                <input
                  type="number"
                  value={newEssay.word_limit}
                  onChange={(e) => setNewEssay({ ...newEssay, word_limit: e.target.value })}
                  placeholder="250"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                />
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setShowAddEssay(false)}
              className="flex-1 px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddEssay}
              disabled={!newEssay.title.trim() && !newEssay.prompt.trim()}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-slate-300 transition-colors"
            >
              Add Essay
            </button>
          </div>
        </div>
      </div>
    );
  };

  const AddMilestoneModal = () => {
    if (!showAddMilestone) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6 w-full max-w-md">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Add Milestone</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={newMilestone.title}
                onChange={(e) => setNewMilestone({ ...newMilestone, title: e.target.value })}
                placeholder="e.g., Submit Application"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
              <input
                type="date"
                value={newMilestone.due_date}
                onChange={(e) => setNewMilestone({ ...newMilestone, due_date: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
              <select
                value={newMilestone.category}
                onChange={(e) => setNewMilestone({ ...newMilestone, category: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
              >
                <option value="applications">Applications</option>
                <option value="essays">Essays</option>
                <option value="test_prep">Test Prep</option>
                <option value="financial_aid">Financial Aid</option>
                <option value="visits">Campus Visits</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
              <textarea
                value={newMilestone.description}
                onChange={(e) => setNewMilestone({ ...newMilestone, description: e.target.value })}
                placeholder="Additional details about this milestone..."
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 resize-none"
              />
            </div>
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setShowAddMilestone(false)}
              className="flex-1 px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddMilestone}
              disabled={!newMilestone.title.trim() || !newMilestone.due_date}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-slate-300 transition-colors"
            >
              Add To-Do
            </button>
          </div>
        </div>
      </div>
    );
  };

  const AddDeadlineModal = () => {
    if (!showAddDeadline) return null;
    
    const handleDeadlineSelect = (deadline) => {
      setNewDeadline({
        title: deadline.title,
        deadline_type: deadline.type,
        description: deadline.description,
        urgency_level: deadline.urgency,
        due_date: '' // User still needs to set the date
      });
    };
    
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Add Deadline for {college.name}</h3>
          
          {/* Quick Select Common Deadlines */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-3">Select Common Deadline</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {commonDeadlines.map((deadline) => (
                <button
                  key={deadline.type}
                  onClick={() => handleDeadlineSelect(deadline)}
                  className={`text-left p-3 rounded-lg border transition-colors ${
                    newDeadline.deadline_type === deadline.type 
                      ? 'border-azure-500 bg-azure-50' 
                      : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
                  }`}
                >
                  <div className="font-medium text-sm text-slate-900">{deadline.title}</div>
                  <div className="text-xs text-slate-600 mt-1">{deadline.description}</div>
                  <div className={`text-xs font-medium mt-1 ${
                    deadline.urgency === 'critical' ? 'text-red-600' :
                    deadline.urgency === 'urgent' ? 'text-orange-600' : 'text-slate-600'
                  }`}>
                    {deadline.urgency} priority
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                value={newDeadline.title}
                onChange={(e) => setNewDeadline({ ...newDeadline, title: e.target.value })}
                placeholder="e.g., Early Decision Application Due"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
              <input
                type="date"
                value={newDeadline.due_date}
                onChange={(e) => setNewDeadline({ ...newDeadline, due_date: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
                autoFocus
              />
            </div>

            {newDeadline.deadline_type === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                <textarea
                  value={newDeadline.description}
                  onChange={(e) => setNewDeadline({ ...newDeadline, description: e.target.value })}
                  placeholder="Describe this deadline..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 resize-none"
                />
              </div>
            )}
          </div>
          
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setShowAddDeadline(false)}
              className="flex-1 px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddDeadline}
              disabled={!newDeadline.title.trim() || !newDeadline.due_date}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-slate-300 transition-colors"
            >
              Add Deadline
            </button>
          </div>
        </div>
      </div>
    );
  };

  const EditableField = ({ field, value, type = 'text', placeholder, multiline = false }) => {
    const isEditing = editing === field;
    
    if (isEditing) {
      return (
        <div className="flex items-start gap-2">
          {multiline ? (
            <textarea
              value={editValues[field] || ''}
              onChange={(e) => setEditValues({ ...editValues, [field]: e.target.value })}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 resize-none"
              rows={3}
              autoFocus
            />
          ) : (
            <input
              type={type}
              value={editValues[field] || ''}
              onChange={(e) => setEditValues({ ...editValues, [field]: e.target.value })}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500"
              autoFocus
            />
          )}
          <button
            onClick={() => saveEdit(field)}
            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          >
            <Save className="w-4 h-4" />
          </button>
          <button
            onClick={cancelEdit}
            className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-start gap-2 group">
        <div className="flex-1">
          {value ? (
            multiline ? (
              <p className="text-slate-700 whitespace-pre-wrap">{value}</p>
            ) : (
              <span className="text-slate-700">{value}</span>
            )
          ) : (
            <span className="text-slate-400 italic">{placeholder}</span>
          )}
        </div>
        <button
          onClick={() => startEditing(field, value)}
          className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-slate-600 transition-all"
        >
          <Edit3 className="w-4 h-4" />
        </button>
      </div>
    );
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'reach': return 'text-red-700 bg-red-50 border-red-200';
      case 'target': return 'text-azure-700 bg-azure-50 border-azure-200';
      case 'safety': return 'text-emerald-700 bg-emerald-50 border-emerald-200';
      default: return 'text-slate-700 bg-slate-50 border-slate-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-emerald-700 bg-emerald-50 border-emerald-200';
      case 'submitted': return 'text-azure-700 bg-azure-50 border-azure-200';
      case 'in_progress': return 'text-amber-700 bg-amber-50 border-amber-200';
      default: return 'text-slate-700 bg-slate-50 border-slate-200';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <Breadcrumb items={[
            { label: 'College Journey', href: '/college', icon: GraduationCap },
            { label: college.name }
          ]} />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-slate-900">{college.name}</h1>
                  {currentUser && (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2 px-2 py-1 bg-azure-50 border border-azure-200 rounded-lg">
                        <div className="w-1.5 h-1.5 bg-azure-500 rounded-full"></div>
                        <span className="text-xs font-medium text-azure-900">
                          {currentUser.displayName || currentUser.name || currentUser.first_name}'s Journey
                        </span>
                      </div>
                      {familyUsers && familyUsers.filter(user => user.type !== 'parent').length > 1 && (
                        <select 
                          className="text-xs border border-stone-300 rounded px-2 py-1 bg-white hover:border-stone-400 focus:outline-none focus:border-azure-500"
                          value={currentUser.id}
                          onChange={(e) => {
                            const selectedUser = familyUsers.find(user => user.id === e.target.value);
                            if (selectedUser) {
                              switchUser(selectedUser);
                            }
                          }}
                        >
                          {familyUsers.filter(user => user.type !== 'parent').map(user => (
                            <option key={user.id} value={user.id}>
                              {user.displayName || user.name || user.first_name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getCategoryColor(college.category)}`}>
                    {COLLEGE_CATEGORIES[college.category]?.label}
                  </span>
                  <span className={`px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(college.application_status)}`}>
                    {college.application_status.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors">
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Strategic Hero Section - moved to top for persistent context */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-950 rounded-xl text-white p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
            {/* College Info */}
            <div className="md:col-span-2">
              <div className="flex items-start gap-4">
                <div className="flex-1">
                  <h2 className="text-3xl font-bold text-white mb-2">{college.name}</h2>
                  <div className="text-sm text-stone-200 mb-2">
                    🎯 {COLLEGE_CATEGORIES[college.category]?.label || 'Target School'} • {college.academic_focus || 'Engineering Focus'}
                  </div>
                  <div className="text-sm text-stone-200">
                    {college.strategic_classification || 'Highly Competitive'} • {college.application_strategy || 'Early Decision Advantage'}
                  </div>
                </div>
              </div>
            </div>
            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">{college.usnwr_ranking ? '#' + college.usnwr_ranking : '--'}</div>
                <div className="text-xs text-stone-200">US News Overall</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">{college.acceptance_rate ? college.acceptance_rate + '%' : '--'}</div>
                <div className="text-xs text-stone-200">Acceptance Rate</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">{college.engineering_ranking ? '#' + college.engineering_ranking : '--'}</div>
                <div className="text-xs text-stone-200">Engineering Rank</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
                <div className="text-xl font-bold text-white">
                  {college.engineering_acceptance_rate ? 
                    (college.engineering_acceptance_rate > 100 ? 
                      (college.engineering_acceptance_rate / 10000).toFixed(1) + '%' :
                      college.engineering_acceptance_rate + '%'
                    ) : '--'}
                </div>
                <div className="text-xs text-stone-200">Engineering Rate</div>
              </div>
            </div>
            {/* Status Badges */}
            <div className="flex flex-col gap-2">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                <div className="text-lg font-bold text-white">{college.competitiveness_score || 'High'}</div>
                <div className="text-xs text-stone-200">Your Competitiveness</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 text-center">
                <div className="text-lg font-bold text-white">{college.recommended_strategy || 'ED+'}</div>
                <div className="text-xs text-stone-200">Best Strategy</div>
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content - Left 3 columns */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* Immediate Priorities - Only show if there are priorities */}
            {immediatePriorities.length > 0 && (
              <div className={`bg-white rounded-xl p-6 ${sectionStyles.urgent}`}>
                <div className="flex items-center gap-2 mb-4">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <h3 className="text-lg font-semibold text-red-900">Immediate Priorities</h3>
                </div>
                
                <div className="space-y-3">
                  {immediatePriorities.map((priority) => (
                    <div key={priority.id || `${priority.type}-${priority.title}`} className="bg-coral-50 border rounded-lg p-4 hover:bg-coral-100 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          priority.urgency === 'critical' ? 'bg-red-600' :
                          priority.urgency === 'urgent' || priority.urgency === 'high' ? 'bg-orange-500' :
                          'bg-yellow-500'
                        }`}></div>
                        <div>
                          <p className="font-medium text-red-900">{priority.title}</p>
                          <p className="text-sm text-red-700 mt-1">{priority.description}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-red-600">
                            <Clock className="w-3 h-3" />
                            <span>Due {parseLocalDate(priority.due_date).toLocaleDateString()}</span>
                            <span>• {priority.type === 'deadline' ? 'Official Deadline' : 'Task'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Your Value Proposition - Personal Strategic Notes */}
            <div className={`bg-white rounded-xl border border-slate-200 p-6 ${sectionStyles.valueProposition}`}>
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-violet-600" />
                <h3 className="text-lg font-semibold text-violet-900">Your Value Proposition</h3>
                <div className="ml-auto text-xs bg-violet-100 text-violet-700 px-2 py-1 rounded-full">Essay Foundation</div>
              </div>
              
              <div className="space-y-6">
                {/* Your Interests */}
                <div>
                  <label className="block text-sm font-medium text-slate-900 mb-2">YOUR INTERESTS</label>
                  {editing === 'interests' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editValues.interests || ''}
                        onChange={(e) => setEditValues({...editValues, interests: e.target.value})}
                        className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                        rows="3"
                        placeholder="What are you genuinely passionate about? What draws you to this field/major?"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave('interests')}
                          className="px-3 py-1 bg-violet-600 text-white text-sm rounded hover:bg-violet-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1 bg-stone-300 text-slate-700 text-sm rounded hover:bg-stone-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => startEditing('interests', college.interests || '')}
                      className="p-3 border border-stone-200 rounded-lg min-h-[80px] cursor-pointer hover:bg-stone-50 transition-colors"
                    >
                      {college.interests ? (
                        <p className="text-slate-700 whitespace-pre-wrap">{college.interests}</p>
                      ) : (
                        <p className="text-stone-400 italic">Click to add your interests and passions...</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Your Experiences */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">YOUR EXPERIENCES</label>
                  <p className="text-xs text-slate-600 mb-2">Think about how your specific background and experiences create value for this school's community that no one else could bring...</p>
                  {editing === 'experiences' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editValues.experiences || ''}
                        onChange={(e) => setEditValues({...editValues, experiences: e.target.value})}
                        className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                        rows="4"
                        placeholder="What unique experiences, challenges, or achievements have shaped you? How do these connect to this school's values?"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave('experiences')}
                          className="px-3 py-1 bg-violet-600 text-white text-sm rounded hover:bg-violet-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1 bg-stone-300 text-slate-700 text-sm rounded hover:bg-stone-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => startEditing('experiences', college.experiences || '')}
                      className="p-3 border border-stone-200 rounded-lg min-h-[100px] cursor-pointer hover:bg-stone-50 transition-colors"
                    >
                      {college.experiences ? (
                        <p className="text-slate-700 whitespace-pre-wrap">{college.experiences}</p>
                      ) : (
                        <p className="text-stone-400 italic">Click to add your unique experiences and background...</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Your Unique Perspectives */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">YOUR UNIQUE PERSPECTIVES</label>
                  {editing === 'unique_perspectives' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editValues.unique_perspectives || ''}
                        onChange={(e) => setEditValues({...editValues, unique_perspectives: e.target.value})}
                        className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                        rows="4"
                        placeholder="What viewpoints, insights, or ways of thinking do you bring that others might not? How does your perspective add value?"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave('unique_perspectives')}
                          className="px-3 py-1 bg-violet-600 text-white text-sm rounded hover:bg-violet-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1 bg-stone-300 text-slate-700 text-sm rounded hover:bg-stone-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => startEditing('unique_perspectives', college.unique_perspectives || '')}
                      className="p-3 border border-stone-200 rounded-lg min-h-[100px] cursor-pointer hover:bg-stone-50 transition-colors"
                    >
                      {college.unique_perspectives ? (
                        <p className="text-slate-700 whitespace-pre-wrap">{college.unique_perspectives}</p>
                      ) : (
                        <p className="text-stone-400 italic">Click to add your unique perspectives and viewpoints...</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Strategic Research - School-Specific Details */}
            <div className={`bg-white rounded-xl border border-slate-200 p-6 ${sectionStyles.research}`}>
              <div className="flex items-center gap-2 mb-6">
                <Search className="w-5 h-5 text-amber-600" />
                <h3 className="text-lg font-semibold text-amber-900">Strategic Research</h3>
                <div className="ml-auto text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">School Intelligence</div>
              </div>
              
              <div className="space-y-6">
                {/* Specific Programs to Mention */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">SPECIFIC PROGRAMS TO MENTION</label>
                  {editing === 'specific_programs' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editValues.specific_programs || ''}
                        onChange={(e) => setEditValues({...editValues, specific_programs: e.target.value})}
                        className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        rows="4"
                        placeholder="List specific programs, majors, tracks, or initiatives that interest you. Be specific with names and details."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave('specific_programs')}
                          className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1 bg-stone-300 text-slate-700 text-sm rounded hover:bg-stone-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => startEditing('specific_programs', college.specific_programs || '')}
                      className="p-3 border border-stone-200 rounded-lg min-h-[100px] cursor-pointer hover:bg-stone-50 transition-colors"
                    >
                      {college.specific_programs ? (
                        <div className="text-slate-700 whitespace-pre-wrap">{college.specific_programs}</div>
                      ) : (
                        <div className="text-stone-400 italic">Click to add specific programs and initiatives to mention...</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Unique Opportunities */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">UNIQUE OPPORTUNITIES</label>
                  {editing === 'unique_opportunities' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editValues.unique_opportunities || ''}
                        onChange={(e) => setEditValues({...editValues, unique_opportunities: e.target.value})}
                        className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        rows="4"
                        placeholder="Research opportunities, study abroad, internships, labs, competitions, or other unique offerings."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave('unique_opportunities')}
                          className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1 bg-stone-300 text-slate-700 text-sm rounded hover:bg-stone-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => startEditing('unique_opportunities', college.unique_opportunities || '')}
                      className="p-3 border border-stone-200 rounded-lg min-h-[100px] cursor-pointer hover:bg-stone-50 transition-colors"
                    >
                      {college.unique_opportunities ? (
                        <div className="text-slate-700 whitespace-pre-wrap">{college.unique_opportunities}</div>
                      ) : (
                        <div className="text-stone-400 italic">Click to add unique opportunities and offerings...</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Cultural Fit */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">CULTURAL FIT</label>
                  {editing === 'cultural_fit' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editValues.cultural_fit || ''}
                        onChange={(e) => setEditValues({...editValues, cultural_fit: e.target.value})}
                        className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        rows="4"
                        placeholder="Campus culture, traditions, student life, values, and community aspects that appeal to you."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave('cultural_fit')}
                          className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1 bg-stone-300 text-slate-700 text-sm rounded hover:bg-stone-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => startEditing('cultural_fit', college.cultural_fit || '')}
                      className="p-3 border border-stone-200 rounded-lg min-h-[100px] cursor-pointer hover:bg-stone-50 transition-colors"
                    >
                      {college.cultural_fit ? (
                        <div className="text-slate-700 whitespace-pre-wrap">{college.cultural_fit}</div>
                      ) : (
                        <div className="text-stone-400 italic">Click to add cultural fit and campus life notes...</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Location Advantages */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">LOCATION ADVANTAGES</label>
                  {editing === 'location_advantages' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editValues.location_advantages || ''}
                        onChange={(e) => setEditValues({...editValues, location_advantages: e.target.value})}
                        className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        rows="4"
                        placeholder="Geographic benefits, industry proximity, climate, city resources, networking opportunities."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave('location_advantages')}
                          className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1 bg-stone-300 text-slate-700 text-sm rounded hover:bg-stone-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => startEditing('location_advantages', college.location_advantages || '')}
                      className="p-3 border border-stone-200 rounded-lg min-h-[100px] cursor-pointer hover:bg-stone-50 transition-colors"
                    >
                      {college.location_advantages ? (
                        <div className="text-slate-700 whitespace-pre-wrap">{college.location_advantages}</div>
                      ) : (
                        <div className="text-stone-400 italic">Click to add location and geographic advantages...</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Specific Faculty/Research */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">SPECIFIC FACULTY/RESEARCH</label>
                  {editing === 'faculty_research' ? (
                    <div className="space-y-2">
                      <textarea
                        value={editValues.faculty_research || ''}
                        onChange={(e) => setEditValues({...editValues, faculty_research: e.target.value})}
                        className="w-full p-3 border border-stone-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                        rows="4"
                        placeholder="Specific professors, research areas, labs, or academic work that interests you. Include names and details."
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave('faculty_research')}
                          className="px-3 py-1 bg-amber-600 text-white text-sm rounded hover:bg-amber-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditing(null)}
                          className="px-3 py-1 bg-stone-300 text-slate-700 text-sm rounded hover:bg-stone-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      onClick={() => startEditing('faculty_research', college.faculty_research || '')}
                      className="p-3 border border-stone-200 rounded-lg min-h-[100px] cursor-pointer hover:bg-stone-50 transition-colors"
                    >
                      {college.faculty_research ? (
                        <div className="text-slate-700 whitespace-pre-wrap">{college.faculty_research}</div>
                      ) : (
                        <div className="text-stone-400 italic">Click to add specific faculty and research interests...</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Strategic Intelligence Grid */}
            <div className={`bg-white rounded-xl border border-slate-200 p-6 ${sectionStyles.strategic}`}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Strategic Intelligence</h3>
                  <span className="text-xs bg-slate-900 text-white px-2 py-1 rounded-full font-medium">BETA Jun 18</span>
                </div>
                <button
                  onClick={() => setShowUpdateData(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 text-white text-xs rounded-lg hover:bg-teal-700 transition-colors font-medium"
                >
                  <Edit3 className="w-3 h-3" />
                  Update Data
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Institutional Priorities - Most Important for Essays */}
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Star className="w-4 h-4 text-violet-800" />
                    <h4 className="font-semibold text-violet-900">What This School Values</h4>
                  </div>
                  {college.institutional_priorities ? (
                    <div className="text-sm text-violet-800 bg-violet-100 p-3 rounded">
                      <p>{college.institutional_priorities}</p>
                      <div className="text-xs text-violet-600 mt-2 italic">💡 Use this in your essays to show alignment</div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-violet-600">
                      <p className="text-sm">No institutional priorities yet</p>
                      <button 
                        onClick={() => setShowUpdateData(true)}
                        className="mt-2 text-xs text-violet-700 hover:text-violet-900 font-medium"
                      >
                        Add what this school values →
                      </button>
                    </div>
                  )}
                </div>

                {/* Your Advantages */}
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Award className="w-4 h-4 text-teal-800" />
                    <h4 className="font-semibold text-teal-900">Your Advantages</h4>
                  </div>
                  {college.student_advantages && college.student_advantages.length > 0 ? (
                    <div>
                      <ul className="space-y-2 text-sm text-teal-800">
                        {college.student_advantages.map((advantage, index) => (
                          <li key={index} className="bg-teal-100 p-2 rounded">• {advantage}</li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-teal-600">
                      <p className="text-sm">No advantages defined yet</p>
                      <button 
                        onClick={() => setShowUpdateData(true)}
                        className="mt-2 text-xs text-teal-700 hover:text-teal-900 font-medium"
                      > 
                        Add your advantages →
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Test Score Competitiveness - Action-Oriented */}
              {(college.sat_range || college.act_range) && (
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-amber-800" />
                    <h4 className="font-semibold text-amber-900">Test Score Strategy</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-amber-900 mb-2">School's Middle 50%:</div>
                      {college.sat_range && <div className="text-sm text-amber-800">SAT: {college.sat_range}</div>}
                      {college.act_range && <div className="text-sm text-amber-800">ACT: {college.act_range}</div>}
                      {college.test_optional && <div className="text-emerald-600 text-sm mt-1">✓ Test Optional</div>}
                    </div>
                    <div className="bg-amber-100 p-3 rounded">
                      <div className="text-xs text-amber-700 font-medium mb-1">📝 Strategy Notes:</div>
                      <div className="text-xs text-amber-700">
                        {college.test_optional 
                          ? "Submit scores if above middle 50%, consider withholding if below"
                          : "Scores required - aim for middle 50% or higher"
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Academic & Admission Statistics */}
            <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${sectionStyles.research}`}>
              <div 
                className="flex items-center justify-between p-6 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleSection('academics')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Academic & Admission Statistics</h3>
                    <p className="text-sm text-slate-600">GPA requirements, graduation rates, and detailed acceptance data</p>
                  </div>
                </div>
                <div className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center">
                  {expandedSections.academics ? <ChevronUp className="w-3 h-3 text-slate-600" /> : <ChevronDown className="w-3 h-3 text-slate-600" />}
                </div>
              </div>
              
              {expandedSections.academics && (
                <div className="px-6 pb-6 bg-amber-50 border-t border-amber-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* GPA & Academic Standards */}
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                        <Award className="w-4 h-4" />
                        Academic Standards
                      </h4>
                      <div className="space-y-2 text-sm">
                        {college.avg_gpa_weighted && <div><span className="text-slate-600">Avg GPA (Weighted):</span> <span className="font-medium">{college.avg_gpa_weighted}</span></div>}
                        {college.avg_gpa_unweighted && <div><span className="text-slate-600">Avg GPA (Unweighted):</span> <span className="font-medium">{college.avg_gpa_unweighted}</span></div>}
                        {college.class_rank_importance && <div><span className="text-slate-600">Class Rank Importance:</span> <span className="font-medium">{college.class_rank_importance}</span></div>}
                        {college.grad_rate_4yr && <div><span className="text-slate-600">4-Year Grad Rate:</span> <span className="font-medium">{college.grad_rate_4yr}%</span></div>}
                        {college.grad_rate_6yr && <div><span className="text-slate-600">6-Year Grad Rate:</span> <span className="font-medium">{college.grad_rate_6yr}%</span></div>}
                        {college.retention_rate && <div><span className="text-slate-600">Retention Rate:</span> <span className="font-medium">{college.retention_rate}%</span></div>}
                      </div>
                    </div>

                    {/* Detailed Acceptance Rates */}
                    <div className="bg-white rounded-lg p-4 border border-amber-200">
                      <h4 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Detailed Acceptance Data
                      </h4>
                      <div className="space-y-2 text-sm">
                        {college.acceptance_rate && <div><span className="text-slate-600">Overall:</span> <span className="font-medium">{college.acceptance_rate}%</span></div>}
                        {college.ed_acceptance_rate && <div><span className="text-slate-600">Early Decision:</span> <span className="font-medium">{college.ed_acceptance_rate > 100 ? (college.ed_acceptance_rate / 100).toFixed(1) : college.ed_acceptance_rate}%</span></div>}
                        {college.ea_acceptance_rate && <div><span className="text-slate-600">Early Action:</span> <span className="font-medium">{college.ea_acceptance_rate > 100 ? (college.ea_acceptance_rate / 100).toFixed(1) : college.ea_acceptance_rate}%</span></div>}
                        {college.rd_acceptance_rate && <div><span className="text-slate-600">Regular Decision:</span> <span className="font-medium">{college.rd_acceptance_rate}%</span></div>}
                        {college.yield_rate && <div><span className="text-slate-600">Yield Rate:</span> <span className="font-medium">{college.yield_rate}%</span></div>}
                        {college.engineering_acceptance_rate && <div><span className="text-slate-600">Engineering:</span> <span className="font-medium">{college.engineering_acceptance_rate > 100 ? (college.engineering_acceptance_rate / 10000).toFixed(1) : college.engineering_acceptance_rate}%</span></div>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Financial Information */}
            <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${sectionStyles.research}`}>
              <div 
                className="flex items-center justify-between p-6 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleSection('financial')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Financial Information</h3>
                    <p className="text-sm text-slate-600">Tuition, aid, and cost information</p>
                  </div>
                </div>
                <div className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center">
                  {expandedSections.financial ? <ChevronUp className="w-3 h-3 text-slate-600" /> : <ChevronDown className="w-3 h-3 text-slate-600" />}
                </div>
              </div>
              
              {expandedSections.financial && (
                <div className="px-6 pb-6 bg-emerald-50 border-t border-emerald-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Tuition & Costs */}
                    <div className="bg-white rounded-lg p-4 border border-emerald-200">
                      <h4 className="font-semibold text-emerald-900 mb-3">Tuition & Costs</h4>
                      <div className="space-y-2 text-sm">
                        {college.tuition && <div><span className="text-slate-600">Tuition:</span> <span className="font-medium">${college.tuition.toLocaleString()}</span></div>}
                        {college.tuition_in_state && college.tuition_in_state < 200000 && <div><span className="text-slate-600">In-State Tuition:</span> <span className="font-medium">${college.tuition_in_state.toLocaleString()}</span></div>}
                        {college.tuition_out_of_state && college.tuition_out_of_state < 200000 && <div><span className="text-slate-600">Out-of-State Tuition:</span> <span className="font-medium">${college.tuition_out_of_state.toLocaleString()}</span></div>}
                        {college.room_board && <div><span className="text-slate-600">Room & Board:</span> <span className="font-medium">${college.room_board.toLocaleString()}</span></div>}
                        {college.total_cost && <div><span className="text-slate-600">Total Cost:</span> <span className="font-medium">${college.total_cost.toLocaleString()}</span></div>}
                      </div>
                    </div>

                    {/* Financial Aid */}
                    <div className="bg-white rounded-lg p-4 border border-emerald-200">
                      <h4 className="font-semibold text-emerald-900 mb-3">Financial Aid</h4>
                      <div className="space-y-2 text-sm">
                        {college.avg_need_aid && <div><span className="text-slate-600">Avg Need-Based Aid:</span> <span className="font-medium">${college.avg_need_aid.toLocaleString()}</span></div>}
                        {college.avg_merit_aid && <div><span className="text-slate-600">Avg Merit Aid:</span> <span className="font-medium">${college.avg_merit_aid.toLocaleString()}</span></div>}
                        {college.percent_receiving_aid && <div><span className="text-slate-600">Students Receiving Aid:</span> <span className="font-medium">{college.percent_receiving_aid}%</span></div>}
                        {college.need_blind && <div className="text-emerald-600">✓ Need-Blind Admissions</div>}
                        {college.meets_full_need && <div className="text-emerald-600">✓ Meets Full Need</div>}
                        {college.no_loan_policy && <div className="text-emerald-600">✓ No-Loan Policy</div>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Essay & Application Requirements */}
            <div className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${sectionStyles.essays}`}>
              <div 
                className="flex items-center justify-between p-6 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleSection('requirements')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Essay & Application Requirements</h3>
                    <p className="text-sm text-slate-600">Essays, platforms, and application components</p>
                  </div>
                </div>
                <div className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center">
                  {expandedSections.requirements ? <ChevronUp className="w-3 h-3 text-slate-600" /> : <ChevronDown className="w-3 h-3 text-slate-600" />}
                </div>
              </div>
              
              {expandedSections.requirements && (
                <div className="px-6 pb-6 bg-indigo-50 border-t border-indigo-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Essay Requirements */}
                    <div className="bg-white rounded-lg p-4 border border-indigo-200">
                      <h4 className="font-semibold text-indigo-900 mb-3">Essay Requirements</h4>
                      <div className="space-y-2 text-sm">
                        {college.num_essays && <div><span className="text-slate-600">Number of Essays:</span> <span className="font-medium">{college.num_essays}</span></div>}
                        {college.essay_prompts && Array.isArray(college.essay_prompts) && college.essay_prompts.length > 0 && (
                          <div>
                            <span className="text-slate-600">Essay Prompts:</span>
                            <ul className="mt-1 space-y-1">
                              {college.essay_prompts.map((prompt, index) => (
                                <li key={index} className="text-xs bg-indigo-50 px-2 py-1 rounded">• {prompt}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {college.video_essay_required && <div className="text-orange-600">⚠ Video Essay Required</div>}
                        {college.portfolio_required && <div className="text-orange-600">⚠ Portfolio Required</div>}
                      </div>
                    </div>

                    {/* Application Components */}
                    <div className="bg-white rounded-lg p-4 border border-indigo-200">
                      <h4 className="font-semibold text-indigo-900 mb-3">Application Components</h4>
                      <div className="space-y-2 text-sm">
                        {college.teacher_recs && <div><span className="text-slate-600">Teacher Recs:</span> <span className="font-medium">{college.teacher_recs}</span></div>}
                        {college.counselor_rec_required && <div className="text-azure-600">✓ Counselor Rec Required</div>}
                        {college.app_platforms && Array.isArray(college.app_platforms) && college.app_platforms.length > 0 && (
                          <div>
                            <span className="text-slate-600">Platforms:</span>
                            <div className="mt-1">
                              {college.app_platforms.map((platform, index) => (
                                <span key={index} className="inline-block text-xs bg-indigo-50 px-2 py-1 rounded mr-1">{platform}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {college.interview_policy && <div><span className="text-slate-600">Interview:</span> <span className="font-medium">{college.interview_policy}</span></div>}
                        {college.tracks_interest && <div className="text-orange-600">⚠ Tracks Demonstrated Interest</div>}
                        {college.resume_required && <div className="text-azure-600">✓ Resume Required</div>}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* AI-Enhanced Insights */}
            <div className={`bg-white rounded-xl border border-slate-200 p-6 ${sectionStyles.valueProposition}`}>
              <div className="flex items-center gap-2 mb-6">
                <Sparkles className="w-5 h-5 text-slate-700" />
                <h3 className="text-lg font-semibold text-slate-900">AI-Enhanced Insights</h3>
                <span className="text-xs bg-slate-900 text-white px-2 py-1 rounded-full font-medium">GPT-4</span>
              </div>

              {/* Campus Culture & Community */}
              {college.campus_culture && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">Campus Culture & Community</h4>
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <div className="text-xs font-medium text-violet-800 mb-1">Campus Culture</div>
                    <div className="text-sm text-violet-900">{college.campus_culture}</div>
                  </div>
                </div>
              )}

              {/* Career Services & Outcomes */}
              {(college.career_services || college.avg_salary || college.employment_rate || college.graduate_school_placement || college.alumni_network) && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">Career Services & Outcomes</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {college.career_services && (
                      <div className="bg-azure-50 border border-azure-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-azure-800 mb-1">Career Services</div>
                        <div className="text-sm text-azure-900">{college.career_services}</div>
                      </div>
                    )}
                    
                    {college.avg_salary && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-emerald-800 mb-1">Average Starting Salary</div>
                        <div className="text-sm text-emerald-900">${college.avg_salary.toLocaleString()}</div>
                      </div>
                    )}
                    
                    {college.employment_rate && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-emerald-800 mb-1">Employment Rate</div>
                        <div className="text-sm text-emerald-900">{college.employment_rate}%</div>
                      </div>
                    )}
                    
                    {college.graduate_school_placement && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-indigo-800 mb-1">Graduate School Placement</div>
                        <div className="text-sm text-indigo-900">{college.graduate_school_placement}</div>
                      </div>
                    )}
                    
                    {college.alumni_network && (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 lg:col-span-2">
                        <div className="text-xs font-medium text-teal-800 mb-1">Alumni Network</div>
                        <div className="text-sm text-teal-900">{college.alumni_network}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Research & Academic Opportunities */}
              {(college.research_opportunities || college.study_abroad || college.honors_program || college.coop_program || college.internship_opportunities || college.entrepreneurship_support) && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">Research & Academic Opportunities</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {college.research_opportunities && (
                      <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-violet-800 mb-1">Research Opportunities</div>
                        <div className="text-sm text-stone-900">{college.research_opportunities}</div>
                      </div>
                    )}
                    
                    {college.study_abroad && (
                      <div className="bg-azure-50 border border-azure-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-azure-800 mb-1">Study Abroad</div>
                        <div className="text-sm text-stone-900">{college.study_abroad}</div>
                      </div>
                    )}
                    
                    {college.honors_program && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-yellow-800 mb-1">Honors Program</div>
                        <div className="text-sm text-stone-900">{college.honors_program}</div>
                      </div>
                    )}
                    
                    {college.coop_program && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-emerald-800 mb-1">Co-op Program</div>
                        <div className="text-sm text-stone-900">{college.coop_program}</div>
                      </div>
                    )}
                    
                    {college.internship_opportunities && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-indigo-800 mb-1">Internship Opportunities</div>
                        <div className="text-sm text-stone-900">{college.internship_opportunities}</div>
                      </div>
                    )}
                    
                    {college.entrepreneurship_support && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-orange-800 mb-1">Entrepreneurship Support</div>
                        <div className="text-sm text-stone-900">{college.entrepreneurship_support}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Application Tips & Advice */}
              {(college.application_tips || college.what_they_look_for || college.common_mistakes || college.interview_advice || college.essay_advice) && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-slate-900 border-b border-slate-200 pb-2 mb-4">Application Tips & Advice</h4>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {college.application_tips && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-red-800 mb-1">Application Tips</div>
                        <div className="text-sm text-red-900">{college.application_tips}</div>
                      </div>
                    )}
                    
                    {college.what_they_look_for && (
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-orange-800 mb-1">What They Look For</div>
                        <div className="text-sm text-orange-900">{college.what_they_look_for}</div>
                      </div>
                    )}
                    
                    {college.common_mistakes && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-yellow-800 mb-1">Common Mistakes</div>
                        <div className="text-sm text-yellow-900">{college.common_mistakes}</div>
                      </div>
                    )}
                    
                    {college.interview_advice && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-emerald-800 mb-1">Interview Advice</div>
                        <div className="text-sm text-emerald-900">{college.interview_advice}</div>
                      </div>
                    )}
                    
                    {college.essay_advice && (
                      <div className="bg-azure-50 border border-azure-200 rounded-lg p-3">
                        <div className="text-xs font-medium text-azure-800 mb-1">Essay Advice</div>
                        <div className="text-sm text-azure-900">{college.essay_advice}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Show message if no AI data yet */}
              {!college.institutional_priorities && !college.campus_culture && !college.career_services && !college.application_tips && (
                <div className="text-center py-8 text-slate-500">
                  <Sparkles className="w-8 h-8 mx-auto mb-3 text-slate-400" />
                  <p className="text-sm mb-2">No AI-enhanced data available yet</p>
                  <p className="text-xs text-slate-400">Use the AI Data Importer to gather comprehensive college insights</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Right column */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => setShowAddDeadline(true)}
                  className="w-full flex items-center gap-3 p-3 text-left bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <Calendar className="w-5 h-5 text-red-600" />
                  <div>
                    <div className="font-medium text-red-900">Add Deadline</div>
                    <div className="text-xs text-red-600">Application, financial aid, etc.</div>
                  </div>
                </button>
                
                <button 
                  onClick={() => setShowAddMilestone(true)}
                  className="w-full flex items-center gap-3 p-3 text-left bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors"
                >
                  <CheckSquare className="w-5 h-5 text-emerald-600" />
                  <div>
                    <div className="font-medium text-emerald-900">Add Task</div>
                    <div className="text-xs text-emerald-600">Track your progress</div>
                  </div>
                </button>
                
                <button 
                  onClick={() => setShowAddEssay(true)}
                  className="w-full flex items-center gap-3 p-3 text-left bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  <PenTool className="w-5 h-5 text-indigo-600" />
                  <div>
                    <div className="font-medium text-indigo-900">Add Essay</div>
                    <div className="text-xs text-indigo-600">Track essay requirements</div>
                  </div>
                </button>
                
                <button 
                  onClick={() => setShowUpdateData(true)}
                  className="w-full flex items-center gap-3 p-3 text-left bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
                >
                  <Edit3 className="w-5 h-5 text-teal-600" />
                  <div>
                    <div className="font-medium text-teal-900">Update Data</div>
                    <div className="text-xs text-teal-600">Edit college information</div>
                  </div>
                </button>

                {/* Navigation Links */}
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <div className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Navigate To</div>
                  
                  <Link 
                    to="/college/colleges"
                    className="w-full flex items-center gap-3 p-3 text-left bg-azure-50 border border-azure-200 rounded-lg hover:bg-azure-100 transition-colors group"
                  >
                    <BarChart3 className="w-5 h-5 text-azure-600" />
                    <div>
                      <div className="font-medium text-azure-900">Data Entry Hub</div>
                      <div className="text-xs text-azure-600">Import & manage all college data</div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-azure-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>

                  <Link 
                    to="/college/academic-profile"
                    className="w-full flex items-center gap-3 p-3 text-left bg-violet-50 border border-violet-200 rounded-lg hover:bg-violet-100 transition-colors group"
                  >
                    <GraduationCap className="w-5 h-5 text-violet-600" />
                    <div>
                      <div className="font-medium text-violet-900">Academic Profile</div>
                      <div className="text-xs text-violet-600">Update student academic information & goals</div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-violet-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>

                  <Link 
                    to="/college"
                    className="w-full flex items-center gap-3 p-3 text-left bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors group"
                  >
                    <GraduationCap className="w-5 h-5 text-slate-600" />
                    <div>
                      <div className="font-medium text-slate-900">College Journey Hub</div>
                      <div className="text-xs text-slate-600">View all schools & overview</div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-slate-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Application Components */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Application Components</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium text-slate-700">Common App Essay</span>
                  <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">Required</span>
                </div>
                
                {college.teacher_recs && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-700">Teacher Recommendations</span>
                    <span className="text-xs px-2 py-1 bg-azure-100 text-azure-700 rounded-full">{college.teacher_recs}</span>
                  </div>
                )}
                
                {college.counselor_rec_required && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-700">Counselor Recommendation</span>
                    <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">Required</span>
                  </div>
                )}
                
                {college.essay_prompts && Array.isArray(college.essay_prompts) && college.essay_prompts.length > 0 && (
                  <div className="p-3 bg-violet-50 rounded-lg">
                    <div className="text-sm font-medium text-violet-900 mb-2">Supplemental Essays</div>
                    <div className="space-y-1">
                      {college.essay_prompts.slice(0, 2).map((prompt, index) => (
                        <div key={index} className="text-xs text-violet-700">• {prompt.substring(0, 50)}...</div>
                      ))}
                      {college.essay_prompts.length > 2 && (
                        <div className="text-xs text-violet-600">+{college.essay_prompts.length - 2} more</div>
                      )}
                    </div>
                  </div>
                )}
                
                {college.interview_policy && college.interview_policy !== 'Not Available' && (
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <span className="text-sm font-medium text-amber-700">Interview</span>
                    <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">{college.interview_policy}</span>
                  </div>
                )}
                
                {college.portfolio_required && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <span className="text-sm font-medium text-slate-700">Portfolio</span>
                    <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full">Required</span>
                  </div>
                )}
              </div>
            </div>

            {/* School Deadlines - Now with inline editing */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-slate-900">School Deadlines</h3>
                  {verificationStatus.deadlines && (
                    <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Verified
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowAddDeadline(true)}
                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-3">
                {displayDeadlines.length > 0 ? (
                  displayDeadlines.map((deadline) => {
                    const deadlineType = deadline.id; // For deadline fields like 'ed_deadline'
                    const isEditing = editingDeadline === deadlineType;

                    return (
                      <div key={deadline.id} className="p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-slate-900">{deadline.title}</div>
                              {!isEditing && (
                                <button
                                  onClick={() => handleEditDeadline(deadlineType, deadline.due_date)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-slate-400 hover:text-azure-600"
                                  title="Edit deadline"
                                >
                                  <Edit3 className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                            {isEditing ? (
                              <div className="mt-2 flex items-center gap-2">
                                <input
                                  type="date"
                                  value={editingDeadlineValue}
                                  onChange={(e) => setEditingDeadlineValue(e.target.value)}
                                  className="px-2 py-1 text-xs border border-azure-300 rounded focus:outline-none focus:ring-2 focus:ring-azure-500"
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleSaveDeadline(deadlineType)}
                                  className="px-2 py-1 text-xs bg-azure-500 text-white rounded hover:bg-azure-600"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEditDeadline}
                                  className="px-2 py-1 text-xs bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="text-xs text-slate-600 mt-1">
                                {parseLocalDate(deadline.due_date).toLocaleDateString()}
                              </div>
                            )}
                            {deadline.description && !isEditing && (
                              <div className="text-xs text-slate-500 mt-1">{deadline.description}</div>
                            )}
                          </div>
                          {!isEditing && (
                            <div className={`text-xs px-2 py-1 rounded-full font-medium ${
                              deadline.urgency_level === 'critical' ? 'bg-red-100 text-red-700' :
                              deadline.urgency_level === 'urgent' ? 'bg-orange-100 text-orange-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {deadline.urgency_level}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4 text-slate-500">
                    <Calendar className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm">No deadlines yet</p>
                    <button
                      onClick={() => setShowAddDeadline(true)}
                      className="text-xs text-azure-600 hover:text-azure-700 mt-1"
                    >
                      Add your first deadline
                    </button>
                  </div>
                )}
              </div>
              {verificationStatus.deadlines && (
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    Deadlines manually verified. Your changes will update related timeline tasks.
                  </p>
                </div>
              )}
            </div>

            {/* College Essays Tracker */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Essays</h3>
                <button 
                  onClick={() => setShowAddEssay(true)}
                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                {collegeEssays.length > 0 ? (
                  collegeEssays.map((essay) => (
                    <div key={essay.id} className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-sm font-medium text-slate-900">{essay.title}</div>
                      {essay.word_limit && (
                        <div className="text-xs text-slate-600 mt-1">{essay.word_limit} words</div>
                      )}
                      <div className={`text-xs px-2 py-1 rounded-full font-medium mt-2 inline-block ${
                        essay.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                        essay.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {essay.status || 'not started'}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-slate-500">
                    <FileText className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm">No essays tracked yet</p>
                    <button 
                      onClick={() => setShowAddEssay(true)}
                      className="text-xs text-azure-600 hover:text-azure-700 mt-1"
                    >
                      Add your first essay
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Timeline</h3>
                <button 
                  onClick={() => setShowAddMilestone(true)}
                  className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                {collegeTimeline.length > 0 ? (
                  collegeTimeline.slice(0, 5).map((item) => (
                    <div key={item.id} className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        item.status === 'completed' ? 'bg-emerald-500' :
                        item.status === 'in_progress' ? 'bg-amber-500' :
                        'bg-slate-300'
                      }`}></div>
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">{item.title}</div>
                        <div className="text-xs text-slate-600">
                          {parseLocalDate(item.due_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4 text-slate-500">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm">No milestones yet</p>
                    <button 
                      onClick={() => setShowAddMilestone(true)}
                      className="text-xs text-azure-600 hover:text-azure-700 mt-1"
                    >
                      Add your first milestone
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <AddMilestoneModal />
      <AddDeadlineModal />
      <AddEssayModal />
      {showUpdateData && (
        <CollegeBatchDataUpdate 
          isOpen={showUpdateData}
          onClose={() => setShowUpdateData(false)}
          prefilterCollege={college}
        />
      )}
    </div>
  );
};

export default CollegeStrategy;